// ناحیه یادداشت مواجهه (FLOW-07؛ US-CLN-020 تا 025): قالب نسخه‌دار قابل تغییر، بخش‌های پیش‌پر، عبارات سریع «/کد» (AC-CLN-021.1)، ذخیره
// خودکار هر ۱۰ ثانیه با نشان «ذخیره شد» و تلاش دوباره پس از قطع ارتباط (AC-CLN-022.2)، امضا با PIN و Ctrl+Enter (AC-CLN-022.1، 082.2)،
// نمای امضاشده با بررسی Hash، الحاقیه (AC-CLN-024.1) و ابطال با دلیل (AC-CLN-025.1).
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type ClnAddendumType,
  type ClnNoteTemplateView,
  type ClnNoteView,
  type ClnNoteVoidReason,
  type ClnQuickPhraseView,
  type ClnTemplateSectionView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ReasonDialog, toJalaliDateTime, useDisplaySettings } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clnKeys, MaybeSample, NoteStatusChip, PinDialog } from './common';
import { SensitiveBadge } from './PsychPanels';

/** فاصله ذخیره خودکار (AC-CLN-022.2). */
export const AUTOSAVE_MS = 10_000;

type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'offline';

/** جایگزینی «/کد» + فاصله در انتهای متن با عبارت سریع (AC-CLN-021.1). */
export function expandQuickPhrase(value: string, phrases: readonly ClnQuickPhraseView[]): string {
  const match = /(^|\s)\/([A-Za-z0-9_؀-ۿ-]{1,20})(\s)$/.exec(value);
  if (!match) {
    return value;
  }
  const phrase = phrases.find((item) => item.shortcut === match[2]);
  if (!phrase) {
    return value;
  }
  return value.slice(0, value.length - match[0].length) + match[1] + phrase.text + match[3];
}

function SectionField({
  section,
  value,
  disabled,
  onChange,
}: {
  section: ClnTemplateSectionView;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation('cln');
  if (section.kind === 'Choice') {
    return (
      <TextField
        select
        label={section.title}
        value={value}
        required={section.required}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        slotProps={{ htmlInput: { 'data-testid': `cln-note-section-${section.key}` } }}
      >
        <MenuItem value="">—</MenuItem>
        {(section.options ?? []).map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    );
  }
  return (
    <TextField
      label={section.title}
      value={value}
      required={section.required}
      disabled={disabled}
      multiline
      minRows={section.kind === 'FreeText' ? 2 : 3}
      helperText={section.kind === 'FreeText' ? undefined : t(`cln:note.section_kind.${section.kind}`)}
      onChange={(event) => onChange(event.target.value)}
      slotProps={{ htmlInput: { maxLength: 20_000, 'data-testid': `cln-note-section-${section.key}` } }}
    />
  );
}

const ADDENDUM_TYPES: readonly ClnAddendumType[] = ['Correction', 'Clarification', 'LateEntry'];
const VOID_REASONS: readonly NonNullable<ClnNoteVoidReason>[] = ['EnteredInError', 'WrongPatient'];

export interface NoteEditorProps {
  encounterId: string;
  /** شمارنده درخواست امضا از بیرون (میانبر Ctrl+Enter صفحه). */
  signRequest?: number;
  onChanged?: () => void;
}

export function NoteEditor({ encounterId, signRequest = 0, onChanged }: NoteEditorProps) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const { timeZone } = useDisplaySettings();
  const queryClient = useQueryClient();
  const addendumKey = useIdempotencyKey();
  const full = access.accessMode === 'Full';
  const tenantId = session?.tenantId;

  const note = useQuery({
    queryKey: clnKeys.note(tenantId, encounterId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/encounters/{id}/note', { params: { path: { id: encounterId } } })),
  });
  const templates = useQuery({
    queryKey: clnKeys.templates(tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/note-templates')),
    staleTime: 60_000,
  });
  const phrases = useQuery({
    queryKey: clnKeys.phrases(tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/quick-phrases')),
    staleTime: 60_000,
  });

  const data = note.data;
  const isDraft = data?.status === 'Draft';
  const canEdit = full && isDraft && data?.isAuthor === true && access.can('cln.note.write');
  // AP-CLN-04 (US-CLN-023): سیاست «نیازمند تأیید» امضای مستقیم را برمی‌دارد؛ ارسال برای تأیید با cln.note.write.
  const canSign = canEdit && access.can('cln.note.sign') && data?.cosignRequired !== true;
  const canSubmitCosign = canEdit;
  const canCosign = full && data?.status === 'PendingCosign' && data.canCosign === true && access.can('cln.note.cosign');
  const [values, setValues] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const dirty = useRef(false);
  const loadedFor = useRef<string | null>(null);

  useEffect(() => {
    if (data && loadedFor.current !== `${data.id}:${data.template.id}:${data.status}`) {
      loadedFor.current = `${data.id}:${data.template.id}:${data.status}`;
      setValues({ ...(data.sections ?? {}) });
      dirty.current = false;
      setSavedAt(data.draftSavedAt);
    }
  }, [data]);

  const saveDraft = useMutation({
    mutationFn: (sections: Record<string, string>) =>
      unwrap(tenantApi.PUT('/api/v1/clinical/notes/{id}/draft', { params: { path: { id: data?.id ?? '' } }, body: { sections } })),
    onMutate: () => setSaveState('saving'),
    onSuccess: (saved) => {
      setSavedAt(saved.draftSavedAt);
      setSaveState(dirty.current ? 'dirty' : 'saved');
    },
    onError: () => {
      // قطع ارتباط یا خطای موقت: متن در حافظه می‌ماند و چرخه بعدی دوباره می‌فرستد.
      dirty.current = true;
      setSaveState('offline');
    },
  });

  const flush = useCallback(async () => {
    if (!canEdit || !dirty.current || !data) {
      return;
    }
    dirty.current = false;
    await saveDraft.mutateAsync({ ...values });
  }, [canEdit, data, saveDraft, values]);

  useEffect(() => {
    if (!canEdit) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      if (dirty.current && !saveDraft.isPending) {
        dirty.current = false;
        saveDraft.mutate({ ...values });
      }
    }, AUTOSAVE_MS);
    return () => window.clearInterval(timer);
  }, [canEdit, saveDraft, values]);

  // C-AGENT-1101: ترک صفحه در فاصله دو چرخه ذخیره خودکار (مثلاً رفتن به پرونده برای پرسشنامه در میانه ویزیت) پیش‌نویس را از دست نمی‌دهد؛ هنگام
  // Unmount آخرین متن فرستاده و در Cache یادداشت گذاشته می‌شود تا بازگشت به مواجهه همان متن را نشان دهد (AC-CLN-022.2).
  const pending = useRef({ values, canEdit, noteId: data?.id });
  pending.current = { values, canEdit, noteId: data?.id };
  useEffect(
    () => () => {
      const { values: sections, canEdit: editable, noteId } = pending.current;
      if (!editable || !noteId || !dirty.current) {
        return;
      }
      dirty.current = false;
      queryClient.setQueryData<typeof data>(clnKeys.note(tenantId, encounterId), (current) => (current ? { ...current, sections: { ...sections } } : current));
      void tenantApi.PUT('/api/v1/clinical/notes/{id}/draft', { params: { path: { id: noteId } }, body: { sections } });
    },
    [queryClient, tenantApi, tenantId, encounterId],
  );

  const update = (key: string, value: string) => {
    const expanded = expandQuickPhrase(value, phrases.data?.items ?? []);
    setValues((current) => ({ ...current, [key]: expanded }));
    dirty.current = true;
    setSaveState('dirty');
  };

  const changeTemplate = useMutation({
    mutationFn: async (templateId: string) => {
      await flush();
      return unwrap(tenantApi.POST('/api/v1/clinical/notes/{id}:change-template', { params: { path: { id: data?.id ?? '' } }, body: { templateId } }));
    },
    onSuccess: (view) => queryClient.setQueryData(clnKeys.note(tenantId, encounterId), view),
  });

  const [signing, setSigning] = useState(false);
  const sign = useMutation({
    mutationFn: async (pin: string) => {
      if (dirty.current) {
        dirty.current = false;
        await saveDraft.mutateAsync({ ...values });
      }
      return unwrap(tenantApi.POST('/api/v1/clinical/notes/{id}:sign', { params: { path: { id: data?.id ?? '' } }, body: { pin, clinicalTime: null } }));
    },
    onSuccess: (view) => {
      setSigning(false);
      queryClient.setQueryData(clnKeys.note(tenantId, encounterId), view);
      void queryClient.invalidateQueries({ queryKey: clnKeys.all(tenantId) });
      onChanged?.();
    },
  });

  const [cosignAction, setCosignAction] = useState<'submit' | 'cosign' | null>(null);
  const [returning, setReturning] = useState(false);
  const cosign = useMutation({
    mutationFn: async (pin: string) => {
      if (cosignAction === 'submit') {
        if (dirty.current) {
          dirty.current = false;
          await saveDraft.mutateAsync({ ...values });
        }
        return unwrap(tenantApi.POST('/api/v1/clinical/notes/{id}:submit-for-cosign', { params: { path: { id: data?.id ?? '' } }, body: { pin, clinicalTime: null } }));
      }
      return unwrap(tenantApi.POST('/api/v1/clinical/notes/{id}:cosign', { params: { path: { id: data?.id ?? '' } }, body: { pin } }));
    },
    onSuccess: (view) => {
      setCosignAction(null);
      queryClient.setQueryData(clnKeys.note(tenantId, encounterId), view);
      void queryClient.invalidateQueries({ queryKey: clnKeys.all(tenantId) });
      onChanged?.();
    },
  });
  const returnNote = useMutation({
    mutationFn: (reason: string) => unwrap(tenantApi.POST('/api/v1/clinical/notes/{id}:return', { params: { path: { id: data?.id ?? '' } }, body: { reason } })),
    onSuccess: (view) => {
      setReturning(false);
      queryClient.setQueryData(clnKeys.note(tenantId, encounterId), view);
      void queryClient.invalidateQueries({ queryKey: clnKeys.all(tenantId) });
      onChanged?.();
    },
  });

  useEffect(() => {
    if (signRequest > 0 && canSign) {
      setSigning(true);
    }
  }, [signRequest, canSign]);

  const [addendum, setAddendum] = useState<{ type: ClnAddendumType; text: string } | null>(null);
  const addAddendum = useMutation({
    mutationFn: (pin: string) =>
      unwrap(
        tenantApi.POST('/api/v1/clinical/notes/{id}/addenda', {
          params: { path: { id: data?.id ?? '' } },
          body: { type: addendum?.type ?? 'Correction', text: addendum?.text.trim() ?? '', pin },
          headers: { 'Idempotency-Key': addendumKey.current() },
        }),
      ),
    onSettled: (_value, error) => addendumKey.settle(error ?? undefined),
    onSuccess: (view) => {
      setAddendum(null);
      queryClient.setQueryData(clnKeys.note(tenantId, encounterId), view);
      onChanged?.();
    },
  });

  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState<NonNullable<ClnNoteVoidReason>>('EnteredInError');
  const voidNote = useMutation({
    mutationFn: (reason: string) =>
      unwrap(tenantApi.POST('/api/v1/clinical/notes/{id}:void', { params: { path: { id: data?.id ?? '' } }, body: { reason: voidReason, note: reason } })),
    onSuccess: (view) => {
      setVoiding(false);
      queryClient.setQueryData(clnKeys.note(tenantId, encounterId), view);
      onChanged?.();
    },
  });

  // یادداشت روان‌درمانی به قالب عمومی برنمی‌گردد (BR-CLN-100؛ سرور PHX-CLN-0105).
  const psychotherapy = data?.kind === 'Psychotherapy';
  const activeTemplates = useMemo(
    () => (templates.data?.items ?? []).filter((template: ClnNoteTemplateView) => template.status === 'Active' && (!psychotherapy || template.kind === 'Psychotherapy')),
    [templates.data, psychotherapy],
  );

  if (note.isPending) {
    return <Paper variant="outlined" sx={{ p: 2 }} data-testid="cln-note-loading" />;
  }
  if (!data) {
    return (
      <Alert severity="error" data-testid="cln-note-error">
        {t('cln:note.load_failed')}
      </Alert>
    );
  }

  const time = (value: string | null | undefined) => (value ? toJalaliDateTime(value, { timeZone }) : '');
  // بدون شبکه TanStack Query جهش را متوقف (Paused) نگه می‌دارد و با برگشت ارتباط می‌فرستد؛ همان نشان «ارتباط قطع است».
  const shownState: SaveState = saveDraft.isPaused ? 'offline' : saveState;
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="cln-note" data-status={data.status}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }} useFlexGap>
        <Typography variant="h3" component="h2">
          {t('cln:note.title')}
        </Typography>
        <NoteStatusChip status={data.status} />
        <MaybeSample isSample={data.template.isSample} />
        {data.kind === 'Psychotherapy' || data.sensitivity === 'HighlySensitive' ? <SensitiveBadge /> : null}
        {canEdit ? (
          <TextField
            select
            size="small"
            label={t('cln:note.template')}
            value={data.template.id}
            onChange={(event) => changeTemplate.mutate(event.target.value)}
            sx={{ minWidth: 220 }}
            slotProps={{ htmlInput: { 'data-testid': 'cln-note-template' } }}
          >
            {(activeTemplates.some((template) => template.id === data.template.id) ? activeTemplates : [data.template, ...activeTemplates]).map((template) => (
              <MenuItem key={template.id} value={template.id}>
                {template.name} · v{template.version}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {data.template.name} · v{data.template.version}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {canEdit && (
          <Typography variant="caption" color={shownState === 'offline' ? 'error' : 'text.secondary'} data-testid="cln-note-save-state" data-state={shownState} aria-live="polite">
            {shownState === 'saving'
              ? t('cln:note.saving')
              : shownState === 'offline'
                ? t('cln:note.offline')
                : shownState === 'dirty'
                  ? t('cln:note.unsaved')
                  : t('cln:note.saved', { time: time(savedAt) })}
          </Typography>
        )}
      </Stack>

      {data.status === 'Voided' && (
        <Alert severity="error" sx={{ mb: 1.5 }} data-testid="cln-note-voided">
          {t('cln:note.voided', { reason: t(`cln:void_reason.${data.voidReason ?? 'EnteredInError'}`), time: time(data.voidedAt) })}
          {data.voidNote ? ` — ${data.voidNote}` : ''}
        </Alert>
      )}
      {data.hashValid === false && (
        <Alert severity="error" sx={{ mb: 1.5 }} data-testid="cln-note-hash-invalid">
          {t('cln:note.hash_invalid')}
        </Alert>
      )}

      {data.contentVisible ? (
        <Stack spacing={1.5} sx={data.status === 'Voided' ? { textDecoration: 'line-through', opacity: 0.7 } : undefined}>
          {data.template.sections.map((section) =>
            canEdit ? (
              <SectionField key={section.key} section={section} value={values[section.key] ?? ''} disabled={!canEdit} onChange={(value) => update(section.key, value)} />
            ) : (
              <Box key={section.key} data-testid={`cln-note-view-${section.key}`}>
                <Typography variant="subtitle2">{section.title}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {data.sections?.[section.key] || '—'}
                </Typography>
              </Box>
            ),
          )}
        </Stack>
      ) : (
        <Alert severity="info" data-testid="cln-note-hidden">
          {t('cln:note.content_hidden')}
        </Alert>
      )}

      {data.status === 'PendingCosign' && (
        <Alert severity="info" sx={{ mt: 1.5 }} data-testid="cln-note-pending-cosign">
          {t('cln:cosign.pending', { time: data.submittedAt ? time(data.submittedAt) : '' })}
        </Alert>
      )}
      {data.status === 'Draft' && data.returnReason && (
        <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="cln-note-returned">
          {t('cln:cosign.returned', { reason: data.returnReason })}
        </Alert>
      )}
      {data.cosignRequired && data.status === 'Draft' && canEdit && (
        <Alert severity="info" sx={{ mt: 1.5 }} data-testid="cln-note-cosign-required">
          {t('cln:cosign.required')}
        </Alert>
      )}
      {data.signedAt && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }} data-testid="cln-note-signature">
          {t('cln:note.signed_by', { name: data.signedByName ?? '—', time: time(data.signedAt) })}
          {data.cosignedAt ? ` · ${t('cln:cosign.cosigned_by', { name: data.cosignedByName ?? '—', time: time(data.cosignedAt) })}` : ''}
          {data.hashValid ? ` · ${t('cln:note.hash_valid')}` : ''}
        </Typography>
      )}

      {data.addenda.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1.5 }} data-testid="cln-note-addenda">
          <Chip size="small" color="info" label={t('cln:note.has_addenda')} sx={{ alignSelf: 'flex-start' }} />
          {data.addenda.map((item) => (
            <Paper key={item.id} variant="outlined" sx={{ p: 1.5 }} data-testid="cln-addendum">
              <Typography variant="subtitle2">
                {t(`cln:addendum_type.${item.type}`)} · {time(item.signedAt)} · {item.authorName ?? ''}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {item.text}
              </Typography>
            </Paper>
          ))}
        </Stack>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }} useFlexGap>
        {canSign && (
          <Button variant="contained" onClick={() => setSigning(true)} data-testid="cln-note-sign" aria-keyshortcuts="Control+Enter">
            {t('cln:note.sign')}
          </Button>
        )}
        {canSubmitCosign && (
          <Button variant={canSign ? 'outlined' : 'contained'} onClick={() => setCosignAction('submit')} data-testid="cln-note-submit-cosign">
            {t('cln:cosign.submit')}
          </Button>
        )}
        {canCosign && (
          <>
            <Button variant="contained" onClick={() => setCosignAction('cosign')} data-testid="cln-note-cosign">
              {t('cln:cosign.approve')}
            </Button>
            <Button onClick={() => setReturning(true)} data-testid="cln-note-return">
              {t('cln:cosign.return')}
            </Button>
          </>
        )}
        {canEdit && (
          <Button onClick={() => void flush()} disabled={saveDraft.isPending} data-testid="cln-note-save">
            {t('cln:note.save_now')}
          </Button>
        )}
        {full && (data.status === 'Signed' || data.status === 'Amended') && access.can('cln.note.addendum') && (
          <Button onClick={() => setAddendum({ type: 'Correction', text: '' })} data-testid="cln-note-addendum">
            {t('cln:note.add_addendum')}
          </Button>
        )}
        {full && data.status !== 'Voided' && data.status !== 'Discarded' && access.can('cln.note.void') && (
          <Button color="error" onClick={() => setVoiding(true)} data-testid="cln-note-void">
            {t('cln:note.void')}
          </Button>
        )}
      </Stack>

      <PinDialog
        open={signing}
        titleKey="cln:sign.title"
        onClose={() => {
          sign.reset();
          setSigning(false);
        }}
        onConfirm={(pin) => sign.mutate(pin)}
        isSubmitting={sign.isPending}
        error={sign.error}
      >
        <Typography variant="body2">{t('cln:sign.message')}</Typography>
      </PinDialog>

      <PinDialog
        open={cosignAction !== null}
        titleKey={cosignAction === 'cosign' ? 'cln:cosign.approve' : 'cln:cosign.submit'}
        onClose={() => {
          cosign.reset();
          setCosignAction(null);
        }}
        onConfirm={(pin) => cosign.mutate(pin)}
        isSubmitting={cosign.isPending}
        error={cosign.error}
      >
        <Typography variant="body2">{t(cosignAction === 'cosign' ? 'cln:cosign.approve_message' : 'cln:cosign.submit_message')}</Typography>
      </PinDialog>
      <ReasonDialog open={returning} titleKey="cln:cosign.return" messageKey="cln:cosign.return_message" busy={returnNote.isPending} error={returnNote.error} onConfirm={(reason) => returnNote.mutate(reason)} onCancel={() => setReturning(false)} />

      <PinDialog
        open={addendum !== null}
        titleKey="cln:note.add_addendum"
        onClose={() => {
          addAddendum.reset();
          setAddendum(null);
        }}
        onConfirm={(pin) => (addendum?.text.trim() ? addAddendum.mutate(pin) : undefined)}
        isSubmitting={addAddendum.isPending}
        error={addAddendum.error}
      >
        <TextField
          select
          label={t('cln:note.addendum_type')}
          value={addendum?.type ?? 'Correction'}
          onChange={(event) => setAddendum((current) => (current ? { ...current, type: event.target.value as ClnAddendumType } : current))}
          slotProps={{ htmlInput: { 'data-testid': 'cln-addendum-type' } }}
        >
          {ADDENDUM_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {t(`cln:addendum_type.${type}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={t('cln:note.addendum_text')}
          value={addendum?.text ?? ''}
          required
          multiline
          minRows={3}
          onChange={(event) => setAddendum((current) => (current ? { ...current, text: event.target.value } : current))}
          slotProps={{ htmlInput: { maxLength: 10_000, 'data-testid': 'cln-addendum-text' } }}
        />
      </PinDialog>

      <ReasonDialog
        open={voiding}
        titleKey="cln:note.void"
        messageKey="cln:note.void_message"
        busy={voidNote.isPending}
        error={voidNote.error}
        onConfirm={(reason) => voidNote.mutate(reason)}
        onCancel={() => setVoiding(false)}
      >
        <TextField
          select
          label={t('cln:note.void_reason')}
          value={voidReason}
          onChange={(event) => setVoidReason(event.target.value as NonNullable<ClnNoteVoidReason>)}
          slotProps={{ htmlInput: { 'data-testid': 'cln-void-reason' } }}
        >
          {VOID_REASONS.map((reason) => (
            <MenuItem key={reason} value={reason}>
              {t(`cln:void_reason.${reason}`)}
            </MenuItem>
          ))}
        </TextField>
      </ReasonDialog>
    </Paper>
  );
}

export type { ClnNoteView };
